import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { languages }   from './data/languages';
import { formsList }   from './features/forms/data/forms';
import { useTranslate } from './hooks/useTranslate';

import Header        from './components/Header';
import LandingView   from './features/i18n/components/LandingView';
import ListView      from './features/forms/components/ListView';
import DetailView, { DetailViewFAB } from './features/forms/components/DetailView';
import OfficeMap       from './features/office-map/components/OfficeMap';

import { FormItem, LangId, ViewName } from './shared/types';
import {
  trackLandingView,
  trackListView,
  trackDetailView,
  trackLanguageSelect,
  trackFormDownload,
  trackTimeOnDetail,
  trackSearch,
} from './shared/analytics';

const App: React.FC = () => {
  // ── 전역 상태 ──────────────────────────────────────────────
  const [currentLang, setCurrentLang]           = useState<LangId>('en');
  const [view, setView]                         = useState<ViewName>('landing');
  const [selectedForm, setSelectedForm]         = useState<FormItem | null>(null);
  const [searchTerm, setSearchTerm]             = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [isDownloading, setIsDownloading]       = useState(false);
  const detailEnterTimeRef                      = useRef<number | null>(null);
  const searchDebounceRef                       = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── 번역 함수 ───────────────────────────────────────────────
  const t = useTranslate(currentLang);

  // ── 서식 필터링 ─────────────────────────────────────────────
  const filteredForms = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return formsList.filter((form) => {
      const matchesSearch =
        t(form.title).toLowerCase().includes(query) ||
        form.title.ko.includes(searchTerm);
      const matchesCategory =
        selectedCategory === 'ALL' || form.cat === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory, t]);

  // ── 검색어 트래킹 (300ms 디바운스) ────────────────────────────
  useEffect(() => {
    if (!searchTerm.trim()) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      trackSearch({ term: searchTerm, resultCount: filteredForms.length, lang: currentLang });
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, filteredForms.length, currentLang]);

  // ── 네비게이션 (메뉴 자동 닫기 포함) ───────────────────────
  const navigateTo = useCallback((nextView: ViewName) => {
    setView(nextView);
  }, []);

  // ── 페이지 뷰 & 체류 시간 트래킹 ───────────────────────────
  useEffect(() => {
    if (view === 'landing') {
      trackLandingView();
    } else if (view === 'list') {
      trackListView(currentLang);
    } else if (view === 'detail' && selectedForm) {
      trackDetailView({
        formId:    selectedForm.id,
        formTitle: selectedForm.title[currentLang] ?? selectedForm.title.ko,
        category:  selectedForm.cat,
        lang:      currentLang,
      });
      detailEnterTimeRef.current = Date.now();
    }

    // 상세에서 벗어날 때 체류 시간 기록
    return () => {
      if (view === 'detail' && selectedForm && detailEnterTimeRef.current) {
        const durationSec = Math.round((Date.now() - detailEnterTimeRef.current) / 1000);
        if (durationSec > 2) {
          trackTimeOnDetail({
            formId:      selectedForm.id,
            formTitle:   selectedForm.title[currentLang] ?? selectedForm.title.ko,
            durationSec,
            lang:        currentLang,
          });
        }
        detailEnterTimeRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedForm]);

  // ── 디테일 진입 시 스크롤 상단 복원 ────────────────────────
  useEffect(() => {
    if (view === 'detail') window.scrollTo({ top: 0, behavior: 'auto' });
  }, [view, selectedForm]);

  // ── 현재 언어 → 영어 → 첫번째 가용 이미지 순 폴백 ─────────
  const resolveImageUrl = useCallback((form: FormItem): string | undefined => {
    return form.images?.[currentLang]
      ?? form.images?.['en']
      ?? Object.values(form.images ?? {}).find(Boolean);
  }, [currentLang]);

  // ── 다운로드 ──────────────────────────────────────────────
  const handleDownload = useCallback(async () => {
    if (isDownloading || !selectedForm) return;

    const imageUrl = resolveImageUrl(selectedForm);
    if (!imageUrl) return;

    trackFormDownload({
      formId:    selectedForm.id,
      formTitle: selectedForm.title[currentLang] ?? selectedForm.title.ko,
      category:  selectedForm.cat,
      lang:      currentLang,
    });

    setIsDownloading(true);
    try {
      const res  = await fetch(imageUrl);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${selectedForm.title[currentLang] ?? selectedForm.title.ko}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, selectedForm, currentLang, resolveImageUrl]);

  // ── 언어 선택 (Landing → List) ─────────────────────────────
  const handleLangSelect = useCallback((lang: LangId) => {
    trackLanguageSelect(lang);
    setCurrentLang(lang);
    navigateTo('list');
  }, [navigateTo]);

  // ── 서식 선택 (List → Detail) ──────────────────────────────
  const handleFormSelect = useCallback((form: FormItem) => {
    setSelectedForm(form);
    navigateTo('detail');
  }, [navigateTo]);

  // ── Landing View ────────────────────────────────────────────
  if (view === 'landing') {
    return (
      <div className="min-h-screen sm:flex sm:items-start sm:justify-center">
        <div className="w-full sm:max-w-md sm:min-h-screen sm:shadow-2xl">
          <LandingView languages={languages} onSelect={handleLangSelect} />
        </div>
      </div>
    );
  }

  // ── App Shell (List / Detail) ───────────────────────────────
  return (
    <div className="sm:flex sm:items-start sm:justify-center sm:min-h-screen">
    <div className="min-h-screen bg-gusring-bg flex flex-col font-sans w-full sm:max-w-md sm:shadow-2xl relative overflow-x-hidden">
      <Header
        view={view}
        currentLang={currentLang}
        languages={languages}
        onBack={() => navigateTo('list')}
        onLanding={() => navigateTo('landing')}
        onMap={() => navigateTo('map')}
      />

      <main className="flex-1">
        {view === 'list' ? (
          <ListView
            t={t}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            filteredForms={filteredForms}
            onFormSelect={handleFormSelect}
            lang={currentLang}
          />
        ) : view === 'map' ? (
          <OfficeMap lang={currentLang} />
        ) : selectedForm ? (
          <DetailView
            form={selectedForm}
            t={t}
            lang={currentLang}
          />
        ) : null}
      </main>

      {/* 상세 화면 FAB */}
      {view === 'detail' && selectedForm && (
        <DetailViewFAB
          t={t}
          isDownloading={isDownloading}
          hasImage={!!resolveImageUrl(selectedForm)}
          onDownload={handleDownload}
        />
      )}

    </div>
    </div>
  );
};

export default App;
